<template>
  <div class="oc-step-site">
    <div class="oc-step-header oc-step-header--compact mb-50">
      <div class="oc-step-header__icon oc-step-header__icon--compact">
        <feather-icon icon="MapPinIcon" size="16" />
      </div>
      <div class="oc-step-header__body">
        <span class="oc-step-header__title">Site &amp; role</span>
        <span class="oc-step-header__hint">
          <template v-if="isEditing">
            · Identity locked — update job title &amp; status
          </template>
          <template v-else>
            · Site code, client &amp; job title (match Client Dashboard)
          </template>
        </span>
      </div>
    </div>

    <!-- Site identity group -->
    <div class="oc-form-section mb-1">
      <div class="oc-form-section__head">
        <feather-icon icon="HomeIcon" size="15" class="mr-50" />
        Site identity
        <span class="oc-form-section__hint">Must match Client Dashboard</span>
      </div>
      <b-row>
        <b-col cols="12" lg="6">
          <b-form-group label-for="oc-site-code" class="oc-field-group">
            <template #label>
              <span class="oc-field-label">Site Code <span class="text-danger">*</span></span>
              <feather-icon
                v-b-tooltip.hover
                icon="HelpCircleIcon"
                size="14"
                class="text-muted ml-25"
                title="Must match the site code used on Client Dashboard. Cannot change after creation."
              />
            </template>
            <b-form-input
              id="oc-site-code"
              v-model="form.site_code"
              :disabled="isSiteCodeDisabled"
              placeholder="radius_synergies"
              required
              :class="{ 'oc-field-filled': form.site_code }"
            />
            <small v-if="isSiteCodeDisabled && canManage" class="oc-field-hint text-muted">
              <feather-icon icon="LockIcon" size="11" class="mr-25" />Locked on edit
            </small>
            <small v-else class="oc-field-hint text-muted">Same value as Client Dashboard site selection.</small>
          </b-form-group>
        </b-col>
        <b-col cols="12" lg="6">
          <b-form-group label-for="oc-site-name" class="oc-field-group">
            <template #label>
              <span class="oc-field-label">Site Name <span class="text-danger">*</span></span>
              <feather-icon
                v-b-tooltip.hover
                icon="HelpCircleIcon"
                size="14"
                class="text-muted ml-25"
                title="Auto-filled from site code + client. Locked when filled."
              />
            </template>
            <b-form-input
              id="oc-site-name"
              v-model="form.site_name"
              :disabled="isSiteNameDisabled"
              placeholder="radius_synergies - Radius Synergies"
              required
              :class="{ 'oc-field-filled': form.site_name }"
              @input="$emit('site-name-edited', $event)"
            />
            <small v-if="isSiteNameDisabled && canManage" class="oc-field-hint text-muted">
              <feather-icon icon="LockIcon" size="11" class="mr-25" />Autofilled — locked
            </small>
            <small v-else class="oc-field-hint text-muted">Built from site code + client.</small>
          </b-form-group>
        </b-col>
        <b-col cols="12" lg="6">
          <b-form-group label-for="oc-client" class="oc-field-group mb-lg-0">
            <template #label>
              <span class="oc-field-label">Client</span>
              <feather-icon
                v-b-tooltip.hover
                icon="HelpCircleIcon"
                size="14"
                class="text-muted ml-25"
                title="Use the client name exactly as it appears on Client Dashboard."
              />
            </template>
            <b-form-input
              id="oc-client"
              v-model="form.client"
              :disabled="isClientDisabled"
              placeholder="Radius Synergies"
              :class="{ 'oc-field-filled': form.client }"
            />
            <small v-if="isClientDisabled && canManage" class="oc-field-hint text-muted">
              <feather-icon icon="LockIcon" size="11" class="mr-25" />Locked on edit
            </small>
            <small v-else class="oc-field-hint text-muted">Match Client Dashboard spelling.</small>
          </b-form-group>
        </b-col>
        <b-col cols="12" lg="6">
          <b-form-group label-for="oc-location" class="oc-field-group mb-lg-0">
            <template #label>
              <span class="oc-field-label">Location</span>
              <feather-icon
                v-b-tooltip.hover
                icon="HelpCircleIcon"
                size="14"
                class="text-muted ml-25"
                title="Locked when filled so site identity stays consistent."
              />
            </template>
            <b-form-input
              id="oc-location"
              v-model="form.location"
              :disabled="isLocationDisabled"
              placeholder="India"
              :class="{ 'oc-field-filled': form.location }"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <!-- Role details group -->
    <div class="oc-form-section mb-1">
      <div class="oc-form-section__head">
        <feather-icon icon="UserIcon" size="15" class="mr-50" />
        Role details
      </div>
      <b-row>
        <b-col cols="12" lg="6">
          <b-form-group label-for="oc-job-title" class="oc-field-group">
            <template #label>
              <span class="oc-field-label">Job Title <span class="text-danger">*</span></span>
            </template>
            <b-form-input
              id="oc-job-title"
              v-model="form.jobTitle"
              :disabled="!canManage"
              placeholder="Field Engineer"
              required
              :class="{ 'oc-field-filled': form.jobTitle }"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" lg="6">
          <b-form-group label-for="oc-project-type" class="oc-field-group mb-lg-0">
            <template #label>
              <span class="oc-field-label">Project Type</span>
            </template>
            <b-form-input
              id="oc-project-type"
              v-model="form.project_type"
              :disabled="!canManage"
              placeholder="Smart Meter Installation"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <!-- Active status -->
    <div class="oc-form-section oc-form-section--status">
      <div class="d-flex align-items-start justify-content-between">
        <div class="pr-1">
          <div class="oc-form-section__head mb-25">
            <feather-icon icon="ToggleRightIcon" size="15" class="mr-50" />
            Template status
          </div>
          <small class="text-muted d-block">
            Inactive templates appear in the list only when the status toggle is set to Inactive, and are not used for new offers.
          </small>
        </div>
        <b-form-checkbox v-model="form.is_active" switch :disabled="!canManage" class="oc-status-switch mb-0">
          <span class="font-weight-bold">{{ form.is_active ? 'Active' : 'Inactive' }}</span>
        </b-form-checkbox>
      </div>
    </div>
  </div>
</template>

<script>
import { VBTooltip } from 'bootstrap-vue'
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
} from 'bootstrap-vue'

export default {
  name: 'OfferConfigureStepSite',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
    canManage: {
      type: Boolean,
      default: false,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    isSiteCodeDisabled: {
      type: Boolean,
      default: false,
    },
    isSiteNameDisabled: {
      type: Boolean,
      default: false,
    },
    isClientDisabled: {
      type: Boolean,
      default: false,
    },
    isLocationDisabled: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style scoped>
.oc-form-section {
  background: #f8f9fb;
  border: 1px solid #eef0f3;
  border-radius: 0.5rem;
  padding: 0.85rem 1rem;
}

.oc-form-section--status {
  background: #fff;
  border-color: #e5e5e5;
}

.oc-form-section__head {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #2c2c2c;
  margin-bottom: 0.75rem;
}

.oc-form-section__hint {
  font-weight: 400;
  font-size: 0.6875rem;
  color: #6e6b7b;
  margin-left: auto;
}

.oc-field-label {
  font-weight: 600;
  font-size: 0.8125rem;
}

.oc-field-hint {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.oc-field-filled >>> .form-control:not(:disabled) {
  border-color: rgba(0, 186, 176, 0.4);
}

.oc-status-switch >>> .custom-control-label {
  font-size: 0.875rem;
  padding-top: 0.1rem;
}
</style>
